import { contracts, createTypedClient, type Fetcher, type FetcherInit, type FetcherResponse } from '@immo/api-contract';
import { env } from '@/lib/env';
import { NetworkError } from '@/lib/errors/network-error';
import { notifyReleaseMismatch } from '@/lib/release-notice';
import { getAccessToken, setAccessToken } from './access-token-store';

// Admin-tier deltas vs apps/web-app's client: X-Immo-Client value, refresh
// path (admin cookie), and the contract walked. See apps/admin-web-app/AGENTS.md.

const API_ORIGIN = env.apiOrigin;

const REFRESH_PATH = contracts.adminAuth.refresh.path;

let refreshInflight: Promise<string | null> | null = null;

const fetcher: Fetcher = async (url, init) => {
  // Proactive refresh: when no in-memory token exists (e.g. right after a
  // browser refresh, before `requireSession` has run), obtain one before
  // issuing the request. Without this, child route loaders that run in
  // parallel with `requireSession` race the session refresh and fire
  // unauthenticated requests. `refreshAccessTokenOnce` deduplicates so
  // parallel loaders share a single refresh round-trip.
  //
  // `proactiveRefreshFailed` tracks whether the proactive attempt
  // returned null — if so, the refresh cookie is gone or rejected and
  // there is no point retrying on a downstream 401. Without this guard,
  // an unauthenticated landing on a protected page fans the failed
  // refresh out into a second wave of refresh + retry across every
  // in-flight loader (the "4 refresh / 2 me / 1 workspaces" cascade).
  let proactiveRefreshFailed = false;

  if (getAccessToken() === null && !url.endsWith(REFRESH_PATH)) {
    proactiveRefreshFailed = (await refreshAccessTokenOnce()) === null;
  }

  const initial = await sendRequest(url, decorate(url, init, getAccessToken()));

  if (initial.status !== 401 || url.endsWith(REFRESH_PATH) || proactiveRefreshFailed) {
    return wrapResponse(initial);
  }

  const refreshed = await refreshAccessTokenOnce();

  if (refreshed === null) {
    return wrapResponse(initial);
  }

  return wrapResponse(await sendRequest(url, decorate(url, init, refreshed)));
};

/**
 * `fetch`, with its transport failures typed. A rejected `fetch` — API
 * unreachable, DNS failure, connection reset, offline, aborted — produces no
 * HTTP response at all. Left unconverted it escapes as a bare
 * `TypeError: Failed to fetch` that every boundary treats as an unknown crash
 * instead of the retryable outage it is.
 */
async function sendRequest(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    throw new NetworkError({ cause });
  }
}

function decorate(url: string, init: FetcherInit, accessToken: string | null): RequestInit {
  const headers: Record<string, string> = {
    ...init.headers,
    'X-Immo-Client': 'admin',
  };

  if (accessToken !== null && !url.endsWith(REFRESH_PATH)) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return {
    method: init.method,
    headers,
    ...(init.body !== undefined ? { body: init.body } : {}),
    credentials: 'include',
  };
}

function wrapResponse(response: Response): FetcherResponse {
  return {
    status: response.status,
    text: () => response.text(),
    header: (name) => response.headers.get(name),
  };
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (refreshInflight !== null) {
    return refreshInflight;
  }

  refreshInflight = (async () => {
    try {
      const result = await apiClient.adminAuth.refresh({ body: {} });

      if (result.ok) {
        setAccessToken(result.body.accessToken);

        return result.body.accessToken;
      }

      setAccessToken(null);

      return null;
    } catch {
      setAccessToken(null);

      return null;
    } finally {
      refreshInflight = null;
    }
  })();

  return refreshInflight;
}

export const apiClient = createTypedClient(contracts, {
  baseUrl: API_ORIGIN,
  fetcher,
  // Both spread rather than assigned: `exactOptionalPropertyTypes` rejects an
  // explicit `undefined`, and an absent release is the normal state in dev and
  // preview, where the comparison must simply never run.
  ...(env.sentry.release !== undefined
    ? { release: env.sentry.release, onReleaseMismatch: notifyReleaseMismatch }
    : {}),
});

export type ApiClient = typeof apiClient;
